import { useCallback, useEffect, useState } from 'react';
import { ENDPOINTS } from 'features/ai-assistant/constants';
import { ALT_API_CLIENT } from 'services/API';
import { UIStatus } from 'UI/constants/status';

const SESSIONS_PER_PAGE = 20;

export const useListConversation = ({
  apiInstance = ALT_API_CLIENT,
  endpoints = ENDPOINTS
} = {}) => {
  const [conversations, setConversations] = useState([]);
  const [uiStatus, setUiStatus] = useState(UIStatus.Loading);
  const [paginationState, setPaginationState] = useState({
    page: 1,
    hasMore: true,
    isLoadingMore: false
  });

  const fetchConversations = useCallback(async () => {
    setUiStatus(UIStatus.Loading);

    try {
      const response = await apiInstance.get(endpoints.sessions, {
        params: {
          page: 1,
          perPage: SESSIONS_PER_PAGE
        }
      });

      const data = response.data?.data || [];
      const total = Number(response.data?.total);
      setConversations(data);
      setUiStatus(data.length > 0 ? UIStatus.Success : UIStatus.Empty);

      const hasMore = SESSIONS_PER_PAGE < total;
      setPaginationState({
        page: 1,
        hasMore,
        isLoadingMore: false
      });
    } catch (error) {
      setUiStatus(UIStatus.Error);
      setPaginationState(prev => ({ ...prev, hasMore: false }));
    }
  }, [apiInstance, endpoints.sessions]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const fetchMoreConversations = useCallback(async () => {
    if (!paginationState.hasMore || paginationState.isLoadingMore) return;

    const nextPage = paginationState.page + 1;
    setPaginationState(prev => ({ ...prev, isLoadingMore: true }));

    try {
      const response = await apiInstance.get(endpoints.sessions, {
        params: {
          page: nextPage,
          perPage: SESSIONS_PER_PAGE
        }
      });

      const newConversations = response.data?.data || [];
      const total = Number(response.data?.total);
      const hasMore = nextPage * SESSIONS_PER_PAGE < total;

      if (newConversations.length > 0) {
        setConversations(prev => [...prev, ...newConversations]);
      }

      setPaginationState({
        page: nextPage,
        hasMore,
        isLoadingMore: false
      });
    } catch (error) {
      setPaginationState(prev => ({ ...prev, isLoadingMore: false }));
    }
  }, [apiInstance, endpoints.sessions, paginationState]);

  const updateConversations = useCallback(action => {
    setConversations(prevConversations => {
      return typeof action === 'function' ? action(prevConversations) : action;
    });
  }, []);

  const pinConversation = useCallback(
    async sessionId => {
      try {
        await apiInstance.put(endpoints.sessionPin.replace(':sessionId', sessionId));
        return true;
      } catch {
        return false;
      }
    },
    [apiInstance, endpoints.sessionPin]
  );

  const unpinConversation = useCallback(
    async sessionId => {
      try {
        await apiInstance.put(endpoints.sessionUnpin.replace(':sessionId', sessionId));
        return true;
      } catch {
        return false;
      }
    },
    [apiInstance, endpoints.sessionUnpin]
  );

  const renameConversation = useCallback(
    async (sessionId, sessionTitle) => {
      try {
        await apiInstance.put(endpoints.sessionUpdate.replace(':sessionId', sessionId), {
          sessionTitle
        });
        return true;
      } catch {
        return false;
      }
    },
    [apiInstance, endpoints.sessionUpdate]
  );

  const deleteConversation = useCallback(
    async sessionId => {
      try {
        await apiInstance.delete(endpoints.sessionDelete.replace(':sessionId', sessionId));
        return true;
      } catch {
        return false;
      }
    },
    [apiInstance, endpoints.sessionDelete]
  );

  return {
    conversations,
    deleteConversation,
    fetchConversations,
    fetchMoreConversations,
    hasMoreConversations: paginationState.hasMore,
    isLoadingMoreConversations: paginationState.isLoadingMore,
    pinConversation,
    renameConversation,
    uiStatus,
    unpinConversation,
    updateConversations
  };
};
